import { FilterType } from 'components/entityViewer/entitiesDropDownFilter/entityFilterControls/FilterType';

// Query keys for remote filter options
export const FILTER_QUERY_KEYS = {
    EXTERNAL_SKU: 'externalSkuSearch',
    VENDORS: 'vendors',
    FACILITIES: 'facilities',
    STATUSES: 'statuses',
    BUYERS: 'buyers',
};

// Static options for assortment categories
export const ASSORTMENT_CATEGORY_OPTIONS = [
    { value: 'PRODUCE', label: 'Produce' },
    { value: 'GROCERY', label: 'Grocery' },
];

// Static options for cross dock filter
export const CROSS_DOCK_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

// Static options for priority filter
export const PRIORITY_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

// Static options for delivery type filter
export const DELIVERY_TYPE_OPTIONS = [
    { value: 'PICK UP', label: 'Pick Up' },
    { value: 'DELIVERED', label: 'Vendor Delivered' },
];

// Static options for has appointment filter
export const HAS_APPOINTMENT_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

/**
 * PO Number filter - text input for partial PO number search
 */
export const poNumberFilterConfig = {
    key: 'poNumber',
    label: 'PO Number',
    type: FilterType.TEXT,
    deletable: true,
    inputProps: {
        placeholder: 'Enter PO number...',
    },
};

/**
 * Vendor filter - multi-select dropdown with search
 */
export const vendorFilterConfig = {
    key: 'vendorIds',
    label: 'Vendor',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [], // Populated dynamically from API
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.VENDORS,
    },
    menuProps: {
        placeholder: 'Search vendors...',
        mergeWithStaticOptions: false,
    },
};

/**
 * Facility filter - multi-select dropdown with search
 */
export const facilityFilterConfig = {
    key: 'facilityIds',
    label: 'Facility',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [], // Populated dynamically from API
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.FACILITIES,
    },
    menuProps: {
        placeholder: 'Search facilities...',
        mergeWithStaticOptions: false,
    },
};

/**
 * Status filter - multi-select dropdown
 */
export const statusFilterConfig = {
    key: 'statuses',
    label: 'Status',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [], // Populated dynamically from API
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.STATUSES,
    },
    menuProps: {
        placeholder: 'Select statuses...',
        mergeWithStaticOptions: false,
    },
};

/**
 * Buyer filter - multi-select dropdown with search
 */
export const buyerFilterConfig = {
    key: 'buyerIds',
    label: 'Buyer',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [], // Populated dynamically from API
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.BUYERS,
    },
    menuProps: {
        placeholder: 'Search buyers...',
        mergeWithStaticOptions: false,
    },
};

/**
 * External SKU filter - multi-select dropdown with remote search
 */
export const externalSkuFilterConfig = {
    key: 'externalSkus',
    label: 'External SKU',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [],
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.EXTERNAL_SKU,
    },
    menuProps: {
        minQueryLength: 2,
        searchDebounceMs: 300,
        emptyRemoteSearchText: 'Type at least 2 characters to search',
        loadingText: 'Searching SKUs...',
        placeholder: 'Search external SKUs...',
        mergeWithStaticOptions: false,
    },
};

/**
 * Delivery Date Range filter
 * API mapping defined in PurchaseOrderTransformConfig.dateRangeKeys
 */
export const deliveryDateRangeFilterConfig = {
    key: 'deliveryDateRange',
    label: 'Delivery Date',
    type: FilterType.DATE_RANGE,
    deletable: true,
};

/**
 * Assortment Category filter - single or multi-select
 */
export const assortmentCategoryFilterConfig = {
    key: 'assortmentCategories',
    label: 'Category',
    type: FilterType.SELECT,
    multi: true,
    options: ASSORTMENT_CATEGORY_OPTIONS,
    deletable: true,
    menuProps: {
        placeholder: 'Select category...',
    },
};

/**
 * Cross Dock filter - boolean select
 */
export const crossDockFilterConfig = {
    key: 'hasCrossDock',
    label: 'Has Cross Dock',
    type: FilterType.SELECT,
    multi: false,
    options: CROSS_DOCK_OPTIONS,
    deletable: true,
    menuProps: {
        placeholder: 'Select...',
    },
};

/**
 * Priority filter - boolean select
 */
export const priorityFilterConfig = {
    key: 'isPriority',
    label: 'Priority',
    type: FilterType.SELECT,
    multi: false,
    options: PRIORITY_OPTIONS,
    deletable: true,
    menuProps: {
        placeholder: 'Select...',
    },
};

/**
 * Delivery Type filter - single select dropdown
 */
export const deliveryTypeFilterConfig = {
    key: 'deliveryType',
    label: 'Delivery Type',
    type: FilterType.SELECT,
    multi: false,
    options: DELIVERY_TYPE_OPTIONS,
    deletable: true,
};

/**
 * Special Instructions filter - text input for partial special instructions search
 */
export const specialInstructionsFilterConfig = {
    key: 'specialInstructions',
    label: 'Special Instructions',
    type: FilterType.TEXT,
    deletable: true,
    inputProps: {
        placeholder: 'Enter special instructions...',
    },
};

/**
 * Has Appointment filter - boolean select
 */
export const hasAppointmentFilterConfig = {
    key: 'hasAppointment',
    label: 'Has Appointment',
    type: FilterType.SELECT,
    multi: false,
    options: HAS_APPOINTMENT_OPTIONS,
    deletable: true,
};

/**
 * Default filter configuration array for the Purchase Orders V2 page.
 * The order here determines the display order in the filter dropdown.
 */
export const PurchaseOrderFilterConfigArray = [
    poNumberFilterConfig,
    deliveryDateRangeFilterConfig,
    facilityFilterConfig,
    vendorFilterConfig,
    statusFilterConfig,
    buyerFilterConfig,
    externalSkuFilterConfig,
    assortmentCategoryFilterConfig,
    crossDockFilterConfig,
    priorityFilterConfig,
    deliveryTypeFilterConfig,
    specialInstructionsFilterConfig,
    hasAppointmentFilterConfig,
];

/**
 * Map of filter keys to their configurations for quick lookup.
 */
export const PurchaseOrderFilterConfigMap = PurchaseOrderFilterConfigArray.reduce((acc, config) => {
    acc[config.key] = config;
    return acc;
}, {});

export const PurchaseOrderSortConfig = {
    sortableFields: {
        id: { apiField: 'id' },
        createdAt: { apiField: 'created_at' },
    },
};

/**
 * Configuration for filter transformations between URL, UI, and API formats.
 * Used with shared filterTransformers utilities (fromUrl, toUrl, pushToUrl, toApi).
 *
 * This config describes the "schema" of filters for the Purchase Orders V2 page,
 * enabling automatic transformations in all three directions:
 * - URL → UI: Restoring filter state from bookmarked/shared URLs
 * - UI → URL: Syncing filter changes to browser history
 * - UI → API: Preparing filters for backend requests
 *
 * @type {import('shared/utils/filterTransformers').FilterTransformConfig}
 *
 * @property {Object} dateRangeKeys - Maps UI date range keys to API start/end params.
 *   The UI stores { start, end } or { startDate, endDate } objects.
 *   The API expects separate query params like ?deliveryDateStart=...&deliveryDateEnd=...
 *
 * @property {string[]} booleanKeys - Filter keys that should be converted to actual booleans.
 *   UI select dropdowns store string values ('true'/'false').
 *   The API expects actual boolean values.
 *
 * @property {string[]} numericArrayKeys - Array filter keys whose values should be parsed as numbers.
 *   URLs store everything as strings; the API expects numeric IDs.
 *
 * @property {string[]} stringArrayKeys - Array filter keys that remain as strings.
 *   These are kept as-is without numeric parsing.
 */
export const PurchaseOrderTransformConfig = {
    dateRangeKeys: {
        deliveryDateRange: { start: 'deliveryDateStart', end: 'deliveryDateEnd' },
    },
    booleanKeys: ['hasCrossDock', 'isPriority', 'hasAppointment'],
    numericArrayKeys: ['vendorIds', 'facilityIds', 'buyerIds', 'externalSkus', 'l1TaxonomyIds', 'l2TaxonomyIds'],
    stringArrayKeys: ['statuses', 'assortmentCategories'],
};
